import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import axios from 'axios'
import jwtDecode from 'jwt-decode'

import Sidebar from '../Sidebar'
import LoggedOutModal from '../AuthPages/LoggedOutModal'
import UserDropdown from '../UserDropdown'
import AppLauncher from '../AppLauncher'

import { useDarkMode } from '../../context/DarkModeContext'
import { useSidebar } from '../../context/SidebarContext'

import { baseURL } from '../../config'

import GeneralSettings from './GeneralSettings'
import DevicesSettings from './DevicesSettings'
import TeamSettings from './TeamSettings'
import SecuritySettings from './SecuritySettings'
import NotificationsSettings from './NotificationsSettings'

export default function SettingsMain() {
	const { isDarkMode } = useDarkMode()
	const navigate = useNavigate()
	const location = useLocation()

	const getTabFromPath = () => {
		const path = location.pathname
		if (path.includes('/settings/subscription')) return 'subscription'
		if (path.includes('/settings/security')) return 'security'
		if (path.includes('/settings/devices')) return 'devices'
		if (path.includes('/settings/team')) return 'team'
		if (path.includes('/settings/telegram')) return 'telegram'
		return 'general'
	}

	const { sidebarWidth } = useSidebar()
	const [user, setUser] = useState(null)
	const [activeTab, setActiveTab] = useState(getTabFromPath())
	const [billingLoading, setBillingLoading] = useState(false)

	const handleTabChange = (e, tab) => {
		e.preventDefault()
		if (tab === 'subscription') {
			setBillingLoading(true)
			handleManageBilling().finally(() => setBillingLoading(false))
			return
		}
		setActiveTab(tab)
		navigate(`/settings${tab === 'general' ? '' : '/' + tab}`)
	}

	useEffect(() => {
		setActiveTab(getTabFromPath())
	}, [location.pathname])

	const handleManageBilling = async () => {
		try {
			if (!user || !user.email) {
				console.error('User email is not available.')
				return
			}

			const teamResponse = await axios.get(`${baseURL}/api/team/my-team`)
			if (teamResponse.data.hasTeam && teamResponse.data.isMember) {
				const shouldRedirect = window.confirm(
					'Your subscription is managed by your team owner. Would you like to view your team settings?'
				)
				if (shouldRedirect) {
					navigate('/settings/team')
				}
				return
			}

			const response = await axios.get(
				`${baseURL}/api/stripe/customer-portal`,
				{
					params: { email: user.email },
				}
			)

			window.location.href = response.data
		} catch (error) {
			console.error('Error redirecting to Stripe Customer Portal', error)
		}
	}

	useEffect(() => {
		try {
			const jwt = localStorage.getItem('jwtToken')
			if (jwt) setUser(jwtDecode(jwt))
		} catch (error) {
			console.error('Error decoding token:', error)
		}
	}, [])

	const renderTabContent = () => {
		switch (activeTab) {
			case 'general':
				return <GeneralSettings isDarkMode={isDarkMode} />
			case 'devices':
				return <DevicesSettings isDarkMode={isDarkMode} activeTab={activeTab} />
			case 'team':
				return <TeamSettings isDarkMode={isDarkMode} activeTab={activeTab} />
			case 'security':
				return <SecuritySettings isDarkMode={isDarkMode} />
			case 'telegram':
				return <NotificationsSettings isDarkMode={isDarkMode} />
			default:
				return null
		}
	}

	const getTabStyle = tabName => {
		const isActive = activeTab === tabName
		const baseClasses =
			'cursor-pointer group flex items-center px-2 py-2 text-base leading-6 font-medium rounded-md transition ease-in-out duration-150'

		if (isDarkMode) {
			return `${baseClasses} ${
				isActive
					? 'text-white bg-gray-700'
					: 'text-gray-300 hover:text-white hover:bg-gray-600'
			} focus:outline-none focus:bg-gray-600`
		} else {
			return `${baseClasses} ${
				isActive
					? 'text-gray-900 bg-gray-200'
					: 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
			} focus:outline-none focus:bg-gray-200`
		}
	}

	return (
		<>
			<div
				style={{
					minHeight: '100vh',
					backgroundColor: isDarkMode ? '#1c2127' : '#ffffff',
				}}
			>
				<Sidebar />

				<div
					style={{
						paddingLeft: `${sidebarWidth}px`,
						transition: 'padding-left 300ms ease-in-out',
					}}
				>
				<div
					className="top-0 z-40 flex h-16 shrink-0 items-center gap-x-6 px-4 sm:px-6 lg:px-8"
					style={{ backgroundColor: isDarkMode ? '#1c2127' : '#ffffff' }}
				>
					<div className="flex items-center ml-auto gap-x-3">
						<AppLauncher />
						<UserDropdown />
					</div>
				</div>

				<main
					className={`flex justify-center p-6 overflow-hidden rounded-tl-lg ${
						isDarkMode ? 'bg-dark-color' : 'bg-white'
					}`}
					style={{ height: 'calc(100vh - 64px)' }}
				>
					{/* Centered container with tabs and content */}
					<div className="w-full max-w-7xl flex gap-3 h-full">
						{/* Tab navigation */}
						<div
							className={`w-64 flex-shrink-0 ${
								isDarkMode
									? 'bg-gray-800/50 border-white/10'
									: 'bg-white border-gray-200'
							} rounded-lg border p-4`}
						>
							<nav className="space-y-1">
								<a
									href="/settings"
									className={getTabStyle('general')}
									onClick={(e) => handleTabChange(e, 'general')}
								>
									<svg
										className={`mr-4 h-6 w-6 ${
											isDarkMode ? 'text-gray-400' : 'text-gray-500'
										} group-hover:text-gray-500 group-focus:text-gray-500 transition ease-in-out duration-150`}
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
											d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
										/>
									</svg>
									General
								</a>

								<a
									href="/settings/subscription"
									className={getTabStyle('subscription')}
									onClick={(e) => handleTabChange(e, 'subscription')}
								>
									<svg
										className={`mr-4 h-6 w-6 ${
											isDarkMode ? 'text-gray-400' : 'text-gray-500'
										} group-hover:text-gray-500 group-focus:text-gray-500 transition ease-in-out duration-150`}
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
											d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z"
										/>
									</svg>
									Subscription
									{billingLoading && (
										<svg
											className="animate-spin ml-2 h-4 w-4 text-blue-500"
											xmlns="http://www.w3.org/2000/svg"
											fill="none"
											viewBox="0 0 24 24"
										>
											<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
											<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
										</svg>
									)}
								</a>

								<a
									href="/settings/devices"
									className={getTabStyle('devices')}
									onClick={(e) => handleTabChange(e, 'devices')}
								>
									<svg
										className={`mr-4 h-6 w-6 ${
											isDarkMode ? 'text-gray-400' : 'text-gray-500'
										} group-hover:text-gray-500 group-focus:text-gray-500 transition ease-in-out duration-150`}
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
											d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
										/>
									</svg>
									Devices
								</a>

								<a
									href="/settings/team"
									className={getTabStyle('team')}
									onClick={(e) => handleTabChange(e, 'team')}
								>
									<svg
										className={`mr-4 h-6 w-6 ${
											isDarkMode ? 'text-gray-400' : 'text-gray-500'
										} group-hover:text-gray-500 group-focus:text-gray-500 transition ease-in-out duration-150`}
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
											d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
										/>
									</svg>
									Team
								</a>
								<a
									href="/settings/security"
									className={getTabStyle('security')}
									onClick={(e) => handleTabChange(e, 'security')}
								>
									<svg
										className={`mr-4 h-6 w-6 ${
											isDarkMode ? 'text-gray-400' : 'text-gray-500'
										} group-hover:text-gray-500 group-focus:text-gray-500 transition ease-in-out duration-150`}
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
											d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
										/>
									</svg>
									Security
								</a>
								<a
									href="/settings/telegram"
									className={getTabStyle('telegram')}
									onClick={(e) => handleTabChange(e, 'telegram')}
								>
									<svg
										className={`mr-4 h-6 w-6 ${
											isDarkMode ? 'text-gray-400' : 'text-gray-500'
										} group-hover:text-gray-500 group-focus:text-gray-500 transition ease-in-out duration-150`}
										fill="currentColor"
										viewBox="0 0 24 24"
									>
										<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" />
									</svg>
									Telegram
								</a>
							</nav>
						</div>

						{/* Tab content */}
						<div
							className={`flex-1 ${
								isDarkMode
									? 'bg-gray-800/50 border-white/10'
									: 'bg-white border-gray-200'
							} rounded-lg border p-6 min-h-[500px] overflow-y-auto scrollbar-hide`}
						>
							{renderTabContent()}
						</div>
					</div>
				</main>
				</div>
			</div>

			<LoggedOutModal />
		</>
	)
}
